import "@/i18n";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, MessageCircle, Home, MapPin } from "lucide-react";
import { buildWhatsAppUrl, isWhatsAppConfigured, WHATSAPP_NUMBER } from "@/lib/whatsapp";
import { RouteMap } from "@/components/RouteMap";
import logoAsset from "@/assets/logo.png.asset.json";
const logo = logoAsset.url;

export const Route = createFileRoute("/booking/status/$id")({
  head: () => ({
    meta: [
      { title: "Thank you — Bacha Way Travel" },
      { name: "description", content: "Your booking request has been received." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BookingStatusPage,
});

export type StoredBooking = {
  ref: string;
  method: "cash" | "online";
  trip_type: "one-way" | "round-trip" | "hourly";
  pickup: { label: string; lat: number; lng: number };
  dropoff: { label: string; lat: number; lng: number };
  distance_km: number;
  duration_min: number;
  polyline?: string;
  travel_date: string;
  travel_time: string;
  passengers: number;
  luggage: number;
  vehicle_name: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  flight_number?: string | null;
  notes?: string | null;
  price_mad: number;
  waMessage?: string;
};

export const storageKey = (id: string) => `bwt:booking:${id}`;

function BookingStatusPage() {
  const { id } = Route.useParams();

  return <BookingStatusContent id={id} />;
}

export function BookingStatusContent({ id }: { id: string }) {
  const { t } = useTranslation();
  const [data, setData] = useState<StoredBooking | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(storageKey(id));
      if (raw) setData(JSON.parse(raw) as StoredBooking);
    } catch {
      // ignore
    }
  }, [id]);

  const ref = data?.ref ?? id.slice(0, 8).toUpperCase();
  const fallbackMsg = t("status.fallbackMsg", { ref });
  const waUrl = isWhatsAppConfigured()
    ? buildWhatsAppUrl(data?.waMessage ?? fallbackMsg)
    : undefined;

  return (
    <div className="mx-auto max-w-xl px-6 py-20 md:py-28 text-center">
      <img src={logo} alt="Bacha Way Travel" className="mx-auto h-14 w-auto mb-8" />

      <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary">
        <Check className="h-8 w-8" />
      </div>

      <h1 className="mt-6 font-display text-3xl md:text-4xl leading-tight">
        {t("status.title")}
      </h1>

      <p className="mt-4 text-base text-muted-foreground">{t("status.soon")}</p>

      <p className="mt-3 text-base text-muted-foreground">{t("status.welcome")}</p>

      <p className="mt-4 text-sm text-muted-foreground">
        {t("status.reference")} <span className="text-primary font-medium">#{ref}</span>
      </p>

      {data?.pickup && data?.dropoff && (
        <div className="mt-8 text-left">
          <RouteMap
            pickup={{ lat: data.pickup.lat, lng: data.pickup.lng }}
            dropoff={{ lat: data.dropoff.lat, lng: data.dropoff.lng }}
            pickupLabel={data.pickup.label}
            dropoffLabel={data.dropoff.label}
            polyline={data.polyline}
            className="w-full h-56 sm:h-72 rounded-2xl overflow-hidden border border-border bg-muted"
          />
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
              <div><span className="text-muted-foreground">A · </span>{data.pickup.label}</div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
              <div><span className="text-muted-foreground">B · </span>{data.dropoff.label}</div>
            </div>
            <div className="text-xs text-muted-foreground pl-6">
              {data.distance_km} km · ~{data.duration_min} min
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <Home className="h-4 w-4" /> {t("status.backHome")}
        </Link>
        {waUrl && (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm hover:bg-muted"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp (+{WHATSAPP_NUMBER})
          </a>
        )}
      </div>
    </div>
  );
}
